/**
 * Runtime zod schemas for every API response shape.
 *
 * The TypeScript interfaces in `types.ts` are compile-time only — they don't
 * catch drift between the backend serializer and the frontend. These schemas
 * mirror the same shapes and are parsed at the API boundary, so a backend
 * rename or a half-applied migration surfaces as a structured `ApiError`
 * (`code: 'INVALID_RESPONSE'`) at the call site instead of a `Cannot read
 * properties of undefined` deep inside the renderer.
 *
 * Keep these in lockstep with `types.ts`. The `satisfies` exports below verify
 * each schema parses to its TS counterpart at compile time.
 */

import { z } from 'zod';
import type {
  Card,
  EncounterEntry,
  EncounterState,
  ExploredCells,
  MapOverlay,
  MapRecord,
  MapSummary,
  OverlayCell,
  Token,
  User,
} from './types.js';

export const UserSchema = z.object({
  id: z.number().int(),
  email: z.string(),
});

export const OverlayCellSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('solid') }),
  z.object({ kind: z.literal('door'), open: z.boolean() }),
  z.object({ kind: z.literal('terrain') }),
]);

export const MapOverlaySchema = z.record(z.string(), OverlayCellSchema);

export const ExploredCellsSchema = z.record(z.string(), z.literal(true));

export const CardSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('rank'),
    rank: z.number().int(),
    suit: z.enum(['spade', 'heart', 'diamond', 'club']),
  }),
  z.object({
    kind: z.literal('joker'),
    color: z.enum(['red', 'black']),
  }),
]);

export const EncounterEntrySchema = z.object({
  tokenId: z.number().int(),
  card: CardSchema,
  pinned: z.boolean().optional(),
});

export const EncounterStateSchema = z.object({
  round: z.number().int(),
  activeTokenId: z.number().int().nullable(),
  order: z.array(EncounterEntrySchema),
});

export const MapRecordSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  image_path: z.string(),
  width_ft: z.number(),
  height_ft: z.number(),
  viewport_x: z.number(),
  viewport_y: z.number(),
  zoom: z.number(),
  overlay: MapOverlaySchema.nullable(),
  explored: ExploredCellsSchema.nullable(),
  encounter: EncounterStateSchema.nullable(),
  created_at: z.string(),
});

export const MapSummarySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  created_at: z.string(),
});

export const MapSummaryListSchema = z.array(MapSummarySchema);

export const TokenSchema = z.object({
  id: z.number().int(),
  map_id: z.number().int(),
  type: z.enum(['pc', 'npc']),
  name: z.string(),
  image_path: z.string().nullable(),
  grid_x: z.number().int(),
  grid_y: z.number().int(),
  visibility_ft: z.number(),
  status_text: z.string().nullable(),
  is_downed: z.boolean(),
  is_placed: z.boolean(),
});

export const TokenListSchema = z.array(TokenSchema);

export const MapWithTokensSchema = z.object({
  map: MapRecordSchema,
  tokens: TokenListSchema,
});

export const AuthMeResponseSchema = z.object({
  user: UserSchema,
  csrf_token: z.string(),
});

/** Public telemetry config the backend exposes to the browser. No secrets. */
export const TelemetryConfigSchema = z.object({
  enabled: z.boolean(),
  serviceName: z.string(),
  sampleRate: z.number(),
});

// Compile-time sanity: each schema's inferred type assigns to its TS sibling.
// If types.ts and these schemas drift, the build fails here.
type _User = z.infer<typeof UserSchema> & User;
type _MapSummary = z.infer<typeof MapSummarySchema> & MapSummary;
type _MapRecord = z.infer<typeof MapRecordSchema> & MapRecord;
type _Token = z.infer<typeof TokenSchema> & Token;
type _OverlayCell = z.infer<typeof OverlayCellSchema> & OverlayCell;
type _MapOverlay = z.infer<typeof MapOverlaySchema> & MapOverlay;
type _ExploredCells = z.infer<typeof ExploredCellsSchema> & ExploredCells;
type _Card = z.infer<typeof CardSchema> & Card;
type _EncounterEntry = z.infer<typeof EncounterEntrySchema> & EncounterEntry;
type _EncounterState = z.infer<typeof EncounterStateSchema> & EncounterState;
// Silence "unused" linter complaints — these are intentional compile-time checks.
export type __SchemaCompatibilityChecks =
  | _User
  | _MapSummary
  | _MapRecord
  | _Token
  | _OverlayCell
  | _MapOverlay
  | _ExploredCells
  | _Card
  | _EncounterEntry
  | _EncounterState;
